// @flow
import { takeEvery, call, put, spawn, select } from 'redux-saga/effects';
import type { Saga } from 'redux-saga';
import pickBy from 'lodash/pickBy';
import filter from 'lodash/filter';
import {
  PRODUCTS,
  receiveProducts,
  receiveMoreProducts,
  receiveAllProducts,
  receiveMoreAllProducts,
  receiveSponsoredProducts,
  failedProductsRequest,
  receiveBestSellingProducts,
  receiveBestSellingAllProducts,
  receiveBestSellingProductsError,
  receiveSearchProducts,
  receiveFeaturedProducts,
  receiveFeaturedProductsError,
  receiveProductRecommendations,
  receiveProductRecommendationsError,
  receiveFavoriteProducts,
  receiveProductRatings,
  receiveRatingGraph,
  receiveProductHasReviewed,
  receiveSubscribableProducts,
  receiveSubscribableProductsError,
  receiveFilteredProducts,
  receiveFilteredProductsError,
} from 'services/actions/productsActions';

import type { RequestProductsAction } from 'services/actions/productsActions';
import {
  getProducts,
  getFavoriteProducts,
  getSponsoredProducts,
  getBestSellingProducts,
  getProductsById,
  getProductRecommendations,
  getRatingsById,
  getRatingGraphDetails,
  getProductHasReviewed,
  getSubscribableProducts,
  getFilteredProducts,
} from 'services/api/products';
import { getCartData } from 'services/api/carts';

import { CATEGORY_ID } from 'utils/categories';
import { CAMPAIGN_TYPE, getCampaignType } from 'utils/campaign';

import type {
  FlattenedProductResponse,
  CartResponse,
  StateProducts,
  Sort,
} from '../../sharedTypes';
import { isPaginatedSortBy } from '../../utils/sort';

const {
  REQUEST_PRODUCTS,
  REQUEST_BEST_SELLING_PRODUCTS,
  REQUEST_PRODUCTS_BY_ID,
  REQUEST_SEARCH_PRODUCTS,
  REQUEST_FEATURED_PRODUCTS,
  REQUEST_PRODUCT_RECOMMENDATIONS,
  RECEIVE_ALL_PRODUCTS,
  RECEIVE_PRODUCTS,
  RECEIVE_FAVORITE_PRODUCTS,
  RECEIVE_MORE_ALL_PRODUCTS,
  RECEIVE_SEARCH_PRODUCTS,
  RECEIVE_FEATURED_PRODUCTS,
  RECEIVE_MORE_PRODUCTS,
  REQUEST_RATING_GRAPH,
  REQUEST_HAS_REVIEWED,
  RECEIVE_BEST_SELLING_PRODUCTS,
  RECEIVE_BEST_SELLING_ALL_PRODUCTS,
  RECEIVE_SPONSORED_PRODUCTS,
  REQUEST_FILTERED_PRODUCTS,
  RECEIVE_FILTERED_PRODUCTS,
} = PRODUCTS;

// watcher sagas
export function* watchFetchProducts(): Saga<void> {
  yield takeEvery(REQUEST_PRODUCTS, fetchProducts);
  yield takeEvery(REQUEST_BEST_SELLING_PRODUCTS, fetchBestSellingProducts);
  yield takeEvery(REQUEST_PRODUCTS_BY_ID, fetchProductsById);
  yield takeEvery(REQUEST_SEARCH_PRODUCTS, fetchSearchProducts);
  yield takeEvery(REQUEST_FEATURED_PRODUCTS, fetchFeaturedProducts);
  yield takeEvery(REQUEST_PRODUCT_RECOMMENDATIONS, fetchProductRecommendations);
  yield takeEvery(REQUEST_RATING_GRAPH, fetchRatingGraphById);
  yield takeEvery(REQUEST_HAS_REVIEWED, fetchProductHasReviewed);
  yield takeEvery(REQUEST_FILTERED_PRODUCTS, fetchFilteredProducts);
  yield takeEvery(
    [
      RECEIVE_ALL_PRODUCTS,
      RECEIVE_PRODUCTS,
      RECEIVE_FAVORITE_PRODUCTS,
      RECEIVE_MORE_ALL_PRODUCTS,
      RECEIVE_SEARCH_PRODUCTS,
      RECEIVE_FEATURED_PRODUCTS,
      RECEIVE_MORE_PRODUCTS,
      RECEIVE_BEST_SELLING_PRODUCTS,
      RECEIVE_BEST_SELLING_ALL_PRODUCTS,
      RECEIVE_SPONSORED_PRODUCTS,
      RECEIVE_FILTERED_PRODUCTS,
    ],
    fetchRatingsById
  );
}

// worker sagas
export function* fetchProducts(action: RequestProductsAction): Saga<void> {
  /* using try catch here so that if this worker SAGA fails,
  we can display failed products ui. */
  const {
    offset,
    selectedCategoryId,
    selectedSort,
    selectedProductLink,
    isAllProducts,
    rateAndReviewFF,
    alternateSubscriptionsFF,
    publicSubscriptionsFF,
  } = action.payload;
  try {
    yield call(
      fetchProductsByCategory,
      offset,
      selectedCategoryId,
      selectedSort,
      selectedProductLink,
      isAllProducts,
      rateAndReviewFF,
      alternateSubscriptionsFF,
      publicSubscriptionsFF
    );
  } catch {
    yield put(failedProductsRequest());
  }
}

function* fetchProductsByCategory(
  offset: number | null,
  selectedCategoryId: string | null,
  selectedSort: Sort,
  selectedProductLink?: string | null = null,
  isAllProducts?: boolean = false,
  rateAndReviewFF: boolean,
  alternateSubscriptionsFF: boolean,
  publicSubscriptionsFF: boolean
): Saga<void> {
  const campaignType = getCampaignType();
  const isShopCampaign = campaignType === CAMPAIGN_TYPE.shop;
  const { loggedIn } = yield select((state) => state.userState);
  /* using spawn here so if fetchSponsoredProducts errors,
  it does not stop fetchProductsByCategory worker from running */
  if (offset === 0 && !isAllProducts) {
    if (loggedIn) yield spawn(fetchSponsoredProducts, rateAndReviewFF);
    // fetch Subscribable Shop products if logged in AND and alternate-subscriptions is ON
    // OR if logged out AND public-subscriptions is ON
    if (
      isShopCampaign &&
      ((loggedIn && alternateSubscriptionsFF) ||
        (!loggedIn && publicSubscriptionsFF))
    )
      yield spawn(fetchSubscribableProducts);
  }

  let data: FlattenedProductResponse;
  const isFavoritesTab = selectedCategoryId === CATEGORY_ID.myfavorites;
  if (selectedCategoryId === CATEGORY_ID.mycart) {
    data = yield call(fetchCartCategoryProducts);
  } else if (isFavoritesTab) {
    data = yield call(getFavoriteProducts, selectedSort);
  } else if (selectedCategoryId === CATEGORY_ID.sponsored) {
    data = {
      productIds: [],
      products: {},
      totalProductCount: 0,
      variants: {},
    };
  } else {
    data = yield call(
      getProducts,
      // we don't paginate best selling sort, so there's no offset in that case
      !isPaginatedSortBy(selectedSort) ? null : offset,
      selectedCategoryId,
      selectedSort,
      selectedProductLink,
      loggedIn
    );
  }
  if (isAllProducts) {
    yield put(receiveAllProducts(data, rateAndReviewFF));
  } else if (offset === 0) {
    if (isFavoritesTab) {
      // we use a different action here because favorite products are paginated
      // we return a list of sorted product ids, and request more products as the user scrolls down the page
      // this is similar to how best selling products are requested
      yield put(receiveFavoriteProducts(data, rateAndReviewFF));
    } else {
      yield put(receiveProducts(data, rateAndReviewFF));
    }
  } else {
    yield put(receiveMoreProducts(data, rateAndReviewFF));
  }
}

function* fetchSponsoredProducts(rateAndReviewFF): Saga<void> {
  const sponsoredProductsResponse: FlattenedProductResponse = yield call(
    getSponsoredProducts
  );
  yield put(
    receiveSponsoredProducts(sponsoredProductsResponse, rateAndReviewFF)
  );
}

function* fetchCartCategoryProducts(): Saga<FlattenedProductResponse> {
  const cartData: CartResponse = yield call(getCartData);
  const { products, variants, cartDetails } = cartData;
  const { variantIds, variantQuantities } = cartDetails;
  // TODO: Resolve the fact that the cart endpoint potentially gives us variants not currently in cart.
  const variantIdsInCart = variantIds.filter((id) => variantQuantities[id] > 0);
  const productIds = sortCartProductIds(products, variantIdsInCart);
  return {
    products,
    variants,
    productIds,
    totalProductCount: productIds.length,
  };
}

function sortCartProductIds(
  products: StateProducts,
  cartVariantIds: Array<string>
): Array<string> {
  const productIds: Array<string> = [];
  for (const id in products) {
    productIds.push(id);
  }
  const sortedProductIds = productIds.sort((a, b) => {
    const productA = products[a];
    const productB = products[b];
    const indexOfFirstVariantInCartA = productA.variantIds.findIndex((el) =>
      cartVariantIds.includes(el)
    );
    const indexOfFirstVariantInCartB = productB.variantIds.findIndex((el) =>
      cartVariantIds.includes(el)
    );
    if (indexOfFirstVariantInCartA === indexOfFirstVariantInCartB) return 0;
    return indexOfFirstVariantInCartA < indexOfFirstVariantInCartB ? -1 : 1;
  });
  return sortedProductIds;
}

function* fetchBestSellingProducts(action: RequestProductsAction) {
  const {
    selectedCategoryId,
    selectedProductLink,
    selectedSort,
    productRecAbKey,
    isAllProducts,
    rateAndReviewFF,
    alternateSubscriptionsFF,
    publicSubscriptionsFF,
  } = action.payload;
  const campaignType = getCampaignType();
  const isShopCampaign = campaignType === CAMPAIGN_TYPE.shop;
  const { loggedIn } = yield select((state) => state.userState);
  if (!isAllProducts) {
    if (loggedIn) yield spawn(fetchSponsoredProducts, rateAndReviewFF);
    // fetch Subscribable Shop products if logged in AND and alternate-subscriptions is ON
    // OR if logged out AND public-subscriptions is ON
    if (
      isShopCampaign &&
      ((loggedIn && alternateSubscriptionsFF) ||
        (!loggedIn && publicSubscriptionsFF))
    )
      yield spawn(fetchSubscribableProducts);
  }
  try {
    const response = yield call(
      getBestSellingProducts,
      selectedCategoryId,
      selectedProductLink,
      selectedSort,
      productRecAbKey,
      loggedIn
    );
    if (isAllProducts) {
      yield put(receiveBestSellingAllProducts(response, rateAndReviewFF));
    } else {
      yield put(receiveBestSellingProducts(response, rateAndReviewFF));
    }
  } catch (err) {
    yield put(receiveBestSellingProductsError(err));
  }
}

function* fetchProductsById(action) {
  const { productIds, isAllProducts, rateAndReviewFF } = action;
  const { loggedIn } = yield select((state) => state.userState);
  const response = yield call(getProductsById, productIds, loggedIn);
  if (isAllProducts) {
    yield put(receiveMoreAllProducts(response, rateAndReviewFF));
  } else {
    yield put(receiveMoreProducts(response, rateAndReviewFF));
  }
}

function* fetchRatingsById(action) {
  const { payload, rateAndReviewFF } = action;
  const { products } = payload;
  const productIds = Object.keys(products);
  if (productIds.length && rateAndReviewFF) {
    const productRatings = yield call(getRatingsById, productIds);
    yield put(receiveProductRatings(productRatings));
  }
}

function* fetchRatingGraphById(action) {
  const { payload: productId } = action;
  if (productId) {
    const ratingGraph = yield call(getRatingGraphDetails, productId);
    yield put(receiveRatingGraph(ratingGraph));
  }
}

function* fetchProductHasReviewed(action) {
  const { payload: productId } = action;
  if (productId) {
    const productHasReviewed = yield call(getProductHasReviewed, productId);
    yield put(receiveProductHasReviewed(productHasReviewed));
  }
}

function* fetchSearchProducts(action) {
  const {
    payload: { productIds, rateAndReviewFF },
  } = action;
  const { loggedIn } = yield select((state) => state.userState);
  const response = yield call(getProductsById, productIds, loggedIn);
  yield put(receiveSearchProducts(response, rateAndReviewFF));
}

function* fetchFeaturedProducts(action) {
  const { selectedCategoryId, rateAndReviewFF } = action;
  const { loggedIn } = yield select((state) => state.userState);
  const response = yield call(
    getProducts,
    null,
    selectedCategoryId,
    'DEFAULT',
    null,
    loggedIn
  );
  try {
    const { products, productIds, variants } = response;
    // we need to filter out any products with order 0 or null
    // featured products have an ordering field of 1 and above
    const filteredProducts = pickBy(products, (p) => p.ordering);
    const filteredProductIds = filter(productIds, (id) => filteredProducts[id]);
    yield put(
      receiveFeaturedProducts(
        {
          products: filteredProducts,
          productIds: filteredProductIds,
          variants,
        },
        rateAndReviewFF
      )
    );
  } catch {
    yield put(receiveFeaturedProductsError());
  }
}

function* fetchProductRecommendations(action) {
  const { productId, campaignType, limit } = action;
  const cartVariantIds = yield select((state) => state.cartState.variantIds);
  try {
    const response = yield call(
      getProductRecommendations,
      productId,
      campaignType,
      limit,
      cartVariantIds
    );
    yield put(receiveProductRecommendations(response));
  } catch (error) {
    yield put(receiveProductRecommendationsError(error));
  }
}

// get refill products
export function* fetchSubscribableProducts() {
  const { loggedIn } = yield select((state) => state.userState);
  try {
    const response = yield call(getSubscribableProducts, loggedIn);
    yield put(receiveSubscribableProducts(response));
  } catch (error) {
    yield put(receiveSubscribableProductsError(error));
  }
}

export function* fetchFilteredProducts(
  filter,
  offset,
  limit,
  orderBy,
  rateAndReviewFF
) {
  try {
    const response = yield call(
      getFilteredProducts,
      filter,
      offset,
      limit,
      orderBy
    );
    yield put(receiveFilteredProducts(response, rateAndReviewFF));
  } catch (err) {
    yield put(receiveFilteredProductsError(err));
  }
}
